/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  EXECUTION: 'execution',
  SUCCESS: 'success',
  DTC_DETECTION_ERROR: 'dtcDetectionError',
  ABNORMAL_ERROR: 'abnormalError',
  ERROR: 'error',
};

export default {
  name: 'requirement059',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      isSession: false,
      isHandlingError: false,
      modal_requirement_059: false,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
    }),
    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.DTC_DETECTION_ERROR:
        case PHASE.ABNORMAL_ERROR:
        case PHASE.ERROR:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        case PHASE.EXECUTION:
        default:
          return 'secondary';
      }
    },

    /** ダイアログヘッダタイトル */
    headerTitle() {
      const titleMap = {
        [PHASE.START]: 'message.common_title_confirmation',
        [PHASE.EXECUTION]: 'individual.common_title_execution',
        [PHASE.SUCCESS]: 'message.common_title_success',
        [PHASE.DTC_DETECTION_ERROR]: 'message.common_title_error',
        [PHASE.ABNORMAL_ERROR]: 'message.common_title_error',
        [PHASE.ERROR]: 'message.common_title_error',
      };
      return this.$t(titleMap[this.phase]);
    },

    /** ダイアログボディメッセージ */
    workSupportMessage() {
      const messageMap = {
        [PHASE.START]: `${this.i18nWsKey}.REQ_WS_59_TXT_1`,
        [PHASE.EXECUTION]: `${this.i18nWsKey}.REQ_WS_59_TXT_2_0001`,
        [PHASE.SUCCESS]: `${this.i18nWsKey}.REQ_WS_59_TXT_3_0002`,
        [PHASE.DTC_DETECTION_ERROR]: `${this.i18nWsKey}.REQ_WS_59_TXT_4_1702`,
        [PHASE.ABNORMAL_ERROR]: `${this.i18nWsKey}.REQ_WS_59_TXT_5`,
        [PHASE.ERROR]: 'individual.error_communication_error',
      };
      return this.$te(messageMap[this.phase]) ? this.$t(messageMap[this.phase]) : messageMap[this.phase];
    },

    /** キャンセルボタン表示 */
    showButtonCancel() {
      return this.phase === PHASE.START;
    },

    /** OKボタン表示 */
    showButtonOk() {
      return this.phase !== PHASE.EXECUTION;
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_059 = true;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_059 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {string} [calledFrom=''] 呼び出し元を示す識別子。'terminate' または 'dispose' を指定した場合、
     *   その他エラー発生時の追加処理（showUnexpectedError）を抑制する
     */
    async validateErrorResult(result, calledFrom = '') {
      await apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        async (apiResult) => {
          apiResult;
          if (calledFrom === 'terminate' || calledFrom === 'dispose') return;
          await this.showUnexpectedError();
        },
      );
    },

    /**
     * API呼び出し
     * @param {String} type APIの種類
     * @returns pollingWspApiの実行結果
     */
    async callApi(type) {
      const token = session.getToken();
      return await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: type
        },
        // エラー発生のコールバック
        async (errorType) => {
          errorType;
          if (type === 'terminate' || type === 'dispose') return;
          await this.showUnexpectedError();
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        async (result) => { await this.validateErrorResult(result, type); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        async (result) => { await this.validateErrorResult(result, type); },
      );
    },

    /** キャンセルボタン押下 */
    onCancelClicked() {
      this.closeWizard();
    },

    /** OKボタン押下 */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.START:
          await this.standby();
          break;
        case PHASE.SUCCESS:
        case PHASE.DTC_DETECTION_ERROR:
        case PHASE.ABNORMAL_ERROR:
        case PHASE.ERROR:
          this.closeWizard();
          break;
        default:
          break;
      }
    },

    /** 停止処理 */
    async stop() {
      await this.callApi('stop');
    },

    /** 終了処理 */
    async terminate() {
      await this.callApi('terminate');
    },

    /** dispose処理 */
    async dispose() {
      await this.callApi('dispose');
    },

    /** 正常終了処理 */
    async finishAsSuccess() {
      await this.stop();
      await this.terminate();
      // 停止画面表示
      this.phase = PHASE.SUCCESS;
    },

    /** DTC検出終了処理 */
    async finishAsDtcDetectionError() {
      await this.stop();
      await this.terminate();
      // DTC検出エラー画面表示
      this.phase = PHASE.DTC_DETECTION_ERROR;
    },

    /** 異常終了処理 */
    async finishAsAbnormalError() {
      // 異常終了の場合、停止処理は実行監視中のみ実行する
      if (this.phase === PHASE.EXECUTION) await this.stop();
      await this.terminate();
      // 異常終了画面表示
      this.phase = PHASE.ABNORMAL_ERROR;
    },

    /** 待機・実行処理 */
    async standby() {
      try {
        this.isLoading = true;

        const standbyResponse = await this.callApi('standby');
        if (!standbyResponse) {
          await this.showUnexpectedError();
          return;
        }

        this.isSession = standbyResponse.data?.user_data?.is_session ?? false;

        // 待機APIのステータスが完了の場合、実行監視処理へ遷移
        if (standbyResponse.data.status === config.STATUS_WSP_COMPLETED) {
          await this.executeMonitoring();
          return;
        }

        if (standbyResponse.data.status === config.STATUS_WSP_FAILED) {
          const asyncCode = standbyResponse.data?.async_code;
          
          // 共通エラーコードのエラーの場合、共通エラー処理を行う
          if (asyncCode === error.WSP_GENERAL_ERROR) {
            await this.showUnexpectedError();
            return;
          }

          // 異常終了エラーコードの場合、異常終了処理を行う
          if (asyncCode === error.WSP_059_ABNORMAL_ERROR) {
            await this.finishAsAbnormalError();
            return;
          }
        }

        await this.showUnexpectedError();
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行監視処理 */
    async executeMonitoring() {
      // 実行監視画面表示
      this.phase = PHASE.EXECUTION;

      const monitorResponse = await this.callApi('monitor');
      if (!monitorResponse) {
        await this.showUnexpectedError();
        return;
      }

      const status = monitorResponse.data?.status;

      // 実行APIのステータスが完了の場合、正常終了処理へ遷移
      if (status === config.STATUS_WSP_COMPLETED) {
        await this.finishAsSuccess();
        return;
      }

      if (status === config.STATUS_WSP_FAILED) {
        const asyncCode = monitorResponse.data?.async_code;
      
        // DTC検出コードのエラーの場合、DTC検出終了処理を行う
        if (asyncCode === error.WSP_059_DTC_DETECTION_ERROR) {
          await this.finishAsDtcDetectionError();
          return;
        }

        // 異常終了エラーコードの場合、異常終了処理を行う
        if (asyncCode === error.WSP_059_ABNORMAL_ERROR) {
          await this.finishAsAbnormalError();
          return;
        }

        await this.showUnexpectedError();
        return;
      }

      await this.showUnexpectedError();
    },

    /** 共通エラー処理 */
    async showUnexpectedError() {
      // 再入を防ぐ（複数箇所から同時に呼ばれるケース）
      if (this.isHandlingError) return;

      this.isHandlingError = true;
      try {
        if (this.isSession) {
          await this.terminate();
        } else {
          await this.dispose();
        }
        // 通信エラー表示
        this.phase = PHASE.ERROR;
      } finally {
        this.isHandlingError = false;
      }
    },
  },

  mounted() {
    this.init();
  },
};